import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap !rounded-none !text-[11px] !font-semibold !uppercase !tracking-[0.16em] ring-offset-background transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary conversion CTA — clay, matches the Home page hero button
        default:
          "bg-clay text-primary-foreground hover:bg-clay-dark",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        // Minimal outlined button on cream/light backgrounds
        outline:
          "border border-current bg-transparent text-current opacity-90 hover:opacity-100 hover:bg-current/10",
        // Charcoal CTA for dark-on-light moments
        secondary:
          "bg-secondary text-secondary-foreground hover:bg-[hsl(var(--charcoal-light))]",
        ghost:
          "text-foreground hover:bg-muted hover:text-foreground",
        link:
          "text-clay !normal-case !tracking-normal !text-sm !font-medium underline-offset-4 hover:underline",
      },
      size: {
        default: "h-11 px-6 py-2.5",
        sm: "h-9 px-4",
        lg: "h-[52px] px-8",
        icon: "h-10 w-10",
      },
    },

    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />;
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
